import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000,
});

let refreshPromise = null;
api.interceptors.response.use((response) => response, async (error) => {
  const original = error.config;
  const isAuthRequest = original?.url?.startsWith("/auth/login") || original?.url?.startsWith("/auth/refresh");
  if (error.response?.status === 401 && original && !original._retried && !isAuthRequest) {
    original._retried = true;
    refreshPromise ||= api.post("/auth/refresh").finally(() => { refreshPromise = null; });
    await refreshPromise;
    return api(original);
  }
  // Retry only idempotent reads, once, for transient network/gateway failures.
  // Mutating requests are never retried automatically, preventing duplicate writes.
  const method = String(original?.method || "get").toLowerCase();
  const transient = !error.response || [502, 503, 504].includes(error.response?.status);
  if (original && method === "get" && transient && !original._networkRetried) {
    original._networkRetried = true;
    await new Promise((resolve) => setTimeout(resolve, 400));
    return api(original);
  }
  return Promise.reject(error);
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
}

export function formatINR(amount) {
  const n = Number(amount || 0);
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
  } catch {
    return iso;
  }
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    const raw = String(iso);
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw.slice(0, 10));
    if (dateOnly) {
      const [y, m, day] = raw.slice(0, 10).split("-");
      return `${day}/${m}/${y}`;
    }
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Kolkata" });
  } catch {
    return iso;
  }
}

export function istDate(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function istMonth(date = new Date()) {
  return istDate(date).slice(0, 7);
}

export function istYear(date = new Date()) {
  return istDate(date).slice(0, 4);
}

export default api;
